import React, { useState, useEffect } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Bot, Save, Loader2 } from "lucide-react";

export default function BotSettingsPage() {
  const [settings, setSettings] = useState({
    enabled: false,
    system_prompt: "",
    fallback_reply: "Auto-reply active. Your message was received.",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await authAxios.get(`${API}/settings/bot`);
        setSettings({
          enabled: data.enabled || false,
          system_prompt: data.system_prompt || "",
          fallback_reply: data.fallback_reply || "Auto-reply active. Your message was received.",
        });
      } catch (err) {
        console.error("Failed to load bot settings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await authAxios.put(`${API}/settings/bot`, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save bot settings:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="h-64 bg-slate-200 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl" data-testid="bot-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#0f172a] flex items-center gap-2">
          <Bot className="w-6 h-6" />
          Bot Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure auto-reply behavior for private chats
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Private Chat Auto-Reply</CardTitle>
              <CardDescription className="mt-1">
                When enabled, the bot will automatically reply to private messages
              </CardDescription>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              data-testid="bot-enabled-toggle"
              className="data-[state=checked]:bg-[#22c55e]"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="system_prompt">System Prompt</Label>
            <Textarea
              id="system_prompt"
              placeholder="You are a helpful customer service agent..."
              value={settings.system_prompt}
              onChange={(e) => setSettings({ ...settings, system_prompt: e.target.value })}
              rows={4}
              data-testid="system-prompt-input"
            />
            <p className="text-xs text-muted-foreground">
              This will be used as the AI system prompt when OpenAI integration is added.
              For now, the fallback reply below is used for all auto-replies.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fallback_reply">Fallback Reply</Label>
            <Textarea
              id="fallback_reply"
              placeholder="Auto-reply message..."
              value={settings.fallback_reply}
              onChange={(e) => setSettings({ ...settings, fallback_reply: e.target.value })}
              rows={3}
              data-testid="fallback-reply-input"
            />
            <p className="text-xs text-muted-foreground">
              This message is sent as the auto-reply for all incoming private messages.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0f172a] hover:bg-[#1e293b]"
              data-testid="save-bot-settings-button"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? "Saving..." : "Save Settings"}
            </Button>
            {saved && (
              <span className="text-sm text-[#22c55e] font-medium" data-testid="save-success">
                Settings saved
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
